/**
 * A base class for handling forms, such as validation.
 */

import CLASS_NAMES from "../constants/css-constants.es6.js";
import { ATTRIBUTES, DOM_EVENTS, HTML_ELEMENTS } from "../constants/html-constants.es6.js"
import {KEYS} from "../constants/js-constants.es6.js";

import AnimationUtils from "../utils/animation-utils.es6.js";
import Paragraph from "../core/paragraph.es6.js";

import CheckboxFormElementParagraphField
  from "../fields/form-element-checkbox-paragraph-field.es6.js";
import EmailFormElementParagraphField
  from "../fields/form-element-email-paragraph-field.es6.js";
import FileFormElementParagraphField
  from "../fields/form-element-file-paragraph-field.es6.js";
import NumberFormElementParagraphField
  from "../fields/form-element-number-paragraph-field.es6.js";
import RadioFormElementParagraphField
  from "../fields/form-element-radio-paragraph-field.es6.js";
import SelectFormElementParagraphField
  from "../fields/form-element-select-paragraph-field.es6.js";
import TelephoneFormElementParagraphField
  from "../fields/form-element-telephone-paragraph-field.es6.js";
import TextFormElementParagraphField
  from "../fields/form-element-text-paragraph-field.es6.js";
import TextareaFormElementParagraphField
  from "../fields/form-element-textarea-paragraph-field.es6.js";
import SubmitFormElementSubmitField
  from "../fields/form-element-submit-paragraph-field.es6.js";

export default class FormParagraph extends Paragraph {
  #scrollToErrors;

  #toggleErrorsByVisibility;

  get inputSubmit() {
    return new SubmitFormElementSubmitField({
      machineName: 'field_form_element_submit',
      parentElement: this,
    });
  }

  get fields() {
    const propertyNames = Object.getOwnPropertyNames(
      Object.getPrototypeOf(this),
    );

    const allowedClassNames = [
      CheckboxFormElementParagraphField,
      EmailFormElementParagraphField,
      FileFormElementParagraphField,
      NumberFormElementParagraphField,
      RadioFormElementParagraphField,
      SelectFormElementParagraphField,
      TelephoneFormElementParagraphField,
      TextFormElementParagraphField,
      TextareaFormElementParagraphField,
    ].map(x => x.className);

    const fields = [];
    propertyNames.forEach(propertyName => {
      const property = this[propertyName];

      // We use a static property called className here on each field because
      // we can't rely on property.constructor.name in minified code.
      const className = property.constructor.className;

      if (!allowedClassNames.includes(className)) {
        return;
      }

      fields.push(property);
    });

    return fields;
  }

  constructor({
    machineName,
    parentElement,
    scrollToErrors = true,
    toggleErrorsByVisibility = false,
  }) {
    super({ machineName, parentElement });

    this.#scrollToErrors = scrollToErrors;
    this.#toggleErrorsByVisibility = toggleErrorsByVisibility;
  }

  updateInputSubmit() {
    const $submitButton = this.inputSubmit.$().find(HTML_ELEMENTS.BUTTON);

    const submitHandler = async () => {
      const valid = this.validate();
      if (!valid) {
        return;
      }

      await this.submit();
    };

    $submitButton.on(DOM_EVENTS.CLICK, async event => {
      event.preventDefault();
      await submitHandler();
    });

    $submitButton.on(DOM_EVENTS.KEYPRESS, async event => {
      if (event.key === KEYS.ENTER) {
        event.preventDefault();
        await submitHandler();
      }
    });
  }

  async submit() {
    this.inputSubmit.paragraph.toggleLoadingState(true);

    const errorRedirectPage = this.inputSubmit.paragraph.errorLink.anchor
      .$(0)
      .attr(ATTRIBUTES.HREF);

    const successRedirectPage = this.inputSubmit.paragraph.successLink.anchor
      .$(0)
      .attr(ATTRIBUTES.HREF);

    const formObject = this.getFormObject();
    await this.sendData(formObject, errorRedirectPage, successRedirectPage); // Implement the sendData method in derived classes.

    this.inputSubmit.paragraph.toggleLoadingState(false);
  }

  validate() {
    let valid = true;

    this.fields.forEach(field => {
      const validField = field.paragraph.validate(field.id);
      if (!validField) {
        valid = false;
      }

      const showFieldError = !validField;

      if (this.#toggleErrorsByVisibility) {
        field.paragraph.toggleErrorByVisibility(showFieldError);
      } else {
        field.paragraph.toggleErrorByDisplay(showFieldError);
      }
    });

    const showError = !valid;

    if (this.#toggleErrorsByVisibility) {
      this.inputSubmit.paragraph.toggleErrorByVisibility(showError);
    } else {
      this.inputSubmit.paragraph.toggleErrorByDisplay(showError);
    }

    if (!valid) {
      if (this.#scrollToErrors) {
        this.scrollToFirstError();
      }
    }

    return valid;
  }

  scrollToFirstError() {
    /*
      Find the first element with the "error" class in the current paragraph.
      If a parent element has a "scroll-target" class set, scroll to that.
      Otherwise, scroll to the closest parent "field" class.  Finally, if that
      closest parent "field" class is part of a fieldset, scroll to the legend
      within the fieldset (if it exists).
    */
    const $error = this.$()
      .find(`.${CLASS_NAMES.ERROR}:visible`)
      .first();

    if (!$error.length) {
      return;
    }

    let $scrollTarget = $error.closest(`.${CLASS_NAMES.SCROLL_TARGET}`).first();

    if (!$scrollTarget.length) {
      $scrollTarget = $error.closest(`.${CLASS_NAMES.FIELD}`).first();

      if ($scrollTarget.length) {
        const $parent = $scrollTarget.parent();
        const parentIsFieldset = $parent.is(HTML_ELEMENTS.FIELDSET);

        if (parentIsFieldset) {
          const $legend = $parent.find(`${HTML_ELEMENTS.LEGEND}:visible`);

          // If a legend exists, scroll to that instead so it doesn't get chopped off.
          if ($legend.length) {
            $scrollTarget = $legend;
          }
        }
      }
    }

    if ($scrollTarget.length) {
      AnimationUtils.scrollToElementWithJQuery({ $element: $scrollTarget });
    }
  }

  getFormObject() {
    const values = {};
    this.fields.forEach(field => {
      values[field.id] = field.paragraph.value;
    });
    return values;
  }
}
